import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useCallback } from "react";

import { NavLink } from "./nav-link";
import { AppLogo } from "./app-logo";
import { Button } from "@/components/ui/button";

const navLinkClass =
  "text-sm font-medium text-neutral-600 transition-colors duration-200 hover:text-neutral-900";

const mobileLinkClass =
  "rounded-md px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => setIsMenuOpen((prev) => !prev), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() => {
        if (window.innerWidth >= 768) setIsMenuOpen(false);
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-100 bg-white/90 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <nav
          className="flex h-16 items-center justify-between"
          role="navigation"
          aria-label="Main navigation"
        >
          <div className="flex items-center gap-8">
            <Link to="/" onClick={closeMenu} aria-label="Arsoc home">
              <AppLogo />
            </Link>

            <div className="hidden items-center gap-6 md:flex">
              <a href="/#services" className={navLinkClass}>
                Services
              </a>
              <NavLink to="/about">About</NavLink>
              <NavLink to="/contact">Contact</NavLink>
            </div>
          </div>

          <div className="hidden md:flex">
            <Button asChild size="sm">
              <Link to="/contact">Talk to us</Link>
            </Button>
          </div>

          <button
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100 md:hidden"
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5 text-neutral-600" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5 text-neutral-600" aria-hidden="true" />
            )}
          </button>
        </nav>

        {isMenuOpen && (
          <div role="dialog" aria-label="Mobile navigation menu" className="md:hidden">
            <div className="flex flex-col space-y-1 border-t border-neutral-100 bg-white py-4">
              <a href="/#services" onClick={closeMenu} className={mobileLinkClass}>
                Services
              </a>
              <Link to="/about" onClick={closeMenu} className={mobileLinkClass}>
                About
              </Link>
              <Link to="/contact" onClick={closeMenu} className={mobileLinkClass}>
                Contact
              </Link>
              <div className="pt-2">
                <Button asChild className="w-full">
                  <Link to="/contact" onClick={closeMenu}>
                    Talk to us
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
